/**
 * Controle dos envios de WhatsApp de uma visita.
 *
 * Responde três perguntas que a equipe faz o tempo todo: saiu? quando? e se
 * não saiu, por quê. O erro mostrado é o texto que a própria Meta devolveu —
 * "número de parâmetros", "número não está na lista de teste" — porque um
 * "falhou" genérico não deixa ninguém consertar nada.
 */
import { useCallback, useEffect, useState } from "react";
import { get, post } from "../lib/api";
import { dataHora } from "../lib/formato";
import { telefoneExibicao } from "../lib/paises";
import { ROTULO_MENSAGEM, type MensagemWhatsapp, type Visita } from "../lib/tipos";

const VERDE = "#0e8c3a";

export function estadoWhatsapp(v: Visita) {
  const msgs = v.mensagens ?? [];
  if (msgs.length === 0) return { icone: "○", cor: "#98a2b3", texto: "Nenhuma mensagem enviada" };

  const confirmou = msgs.some((m) => m.tipo === "CONFIRMACAO" && m.ok);
  const lembrou = msgs.some((m) => m.tipo === "LEMBRETE" && m.ok);
  const convidou = msgs.some((m) => m.tipo === "RECALL" && m.ok);
  const falhou = msgs.some((m) => !m.ok);

  if (convidou) return { icone: "★", cor: VERDE, texto: "Convite de matrícula enviado" };
  if (confirmou && lembrou) return { icone: "✓✓", cor: VERDE, texto: "Confirmação e lembrete enviados" };
  if (confirmou) return { icone: "✓", cor: VERDE, texto: "Confirmação enviada" };
  if (falhou) return { icone: "!", cor: "#d92d20", texto: "Falha no envio — clique para ver o motivo" };
  return { icone: "○", cor: "#98a2b3", texto: "Nenhuma mensagem enviada" };
}

export function HistoricoWhatsapp({
  visita, onFechar, aoEnviar,
}: {
  visita: Visita;
  onFechar: () => void;
  aoEnviar: () => void | Promise<void>;
}) {
  const [msgs, setMsgs] = useState<MensagemWhatsapp[]>(visita.mensagens ?? []);
  const [ocupado, setOcupado] = useState("");
  const [aviso, setAviso] = useState("");

  const recarregar = useCallback(async () => {
    try {
      setMsgs(await get<MensagemWhatsapp[]>(`/api/visitas/${visita.id}/mensagens`));
    } catch { /* mantém o que já está na tela */ }
  }, [visita.id]);

  useEffect(() => { void recarregar(); }, [recarregar]);

  async function enviar(tipo: MensagemWhatsapp["tipo"]) {
    setOcupado(tipo);
    setAviso("");
    try {
      await post(`/api/visitas/${visita.id}/whatsapp`, { tipo });
      setAviso("Mensagem enviada.");
      await recarregar();
      await aoEnviar();
    } catch (e) {
      setAviso(e instanceof Error ? e.message : "Falha no envio");
      await recarregar();
    } finally {
      setOcupado("");
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 950, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 560, maxHeight: "88vh", overflow: "auto", boxShadow: "0 24px 80px rgba(0,0,0,.25)" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #eaecf0", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>Envios de WhatsApp</div>
            <div style={{ fontSize: 12, color: "var(--texto-suave)", marginTop: 2 }}>
              {visita.nomeAluno} — {visita.nomeResponsavel} · {telefoneExibicao(visita.telefoneResponsavel, visita.paisTelefone)}
            </div>
          </div>
          <button onClick={onFechar} aria-label="Fechar"
            style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#667" }}>✕</button>
        </div>

        <div style={{ padding: "16px 20px" }}>
          {msgs.length === 0 ? (
            <div style={{ fontSize: 13, color: "var(--texto-suave)", padding: "12px 0" }}>
              Nenhuma mensagem foi enviada para esta visita ainda.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {msgs.map((m) => (
                <div key={m.id} style={{ border: `1px solid ${m.ok ? "#c6e9d4" : "#fecdca"}`, background: m.ok ? "#f0faf4" : "#fef3f2", borderRadius: 8, padding: "10px 12px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                    <strong style={{ fontSize: 13, color: m.ok ? "#1a5c2e" : "#b42318" }}>
                      {m.ok ? "✓" : "✕"} {ROTULO_MENSAGEM[m.tipo]}
                    </strong>
                    <span style={{ fontSize: 11, color: "var(--texto-suave)" }}>
                      {dataHora(m.enviadoEm ?? m.criadoEm)}
                      {m.tentativas > 1 && ` · ${m.tentativas} tentativas`}
                    </span>
                  </div>
                  {m.erro && (
                    <div style={{ fontSize: 11, color: "#b42318", marginTop: 6, wordBreak: "break-word" }}>
                      {m.erro}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {aviso && <div style={{ fontSize: 12, marginTop: 14, color: "#344054" }}>{aviso}</div>}

          <div style={{ display: "flex", gap: 8, marginTop: 18, flexWrap: "wrap" }}>
            <button className="botao" disabled={!!ocupado} onClick={() => enviar("CONFIRMACAO")}>
              {ocupado === "CONFIRMACAO" ? "Enviando..." : "Enviar confirmação"}
            </button>
            <button className="botao secundario" disabled={!!ocupado} onClick={() => enviar("LEMBRETE")}>
              {ocupado === "LEMBRETE" ? "Enviando..." : "Enviar lembrete agora"}
            </button>
            <button className="botao secundario" disabled={!!ocupado} onClick={() => enviar("RECALL")}>
              {ocupado === "RECALL" ? "Enviando..." : "Enviar convite de matrícula"}
            </button>
          </div>

          <p style={{ fontSize: 11, color: "var(--texto-suave)", marginTop: 14, lineHeight: 1.6 }}>
            O reenvio ignora o controle de repetição — use quando a família disser que não recebeu.
            O lembrete automático continua saindo sozinho 3 horas antes, das 8h às 21h.
          </p>
        </div>
      </div>
    </div>
  );
}
